import React, { useEffect } from 'react';

// tremor
import { Subtitle } from "@tremor/react";

// bootstrap
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";

// styling
import { Colors, Spacing } from "_styles";

const MAC_ADDRESS_REGEX = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;

const LocationBeaconForm = ({ 
    beaconDetails, 
    setBeaconDetails, 
    airportUuid,
    initialBeaconDetails = null 
}) => {

    // Load initial beacon details if provided (for edit mode)
    useEffect(() => {
        if (initialBeaconDetails && Array.isArray(initialBeaconDetails) && initialBeaconDetails.length > 0) {
            setBeaconDetails(initialBeaconDetails);
        }
    }, [initialBeaconDetails]);

    // Add a new beacon
    const handleAddBeacon = () => {
        setBeaconDetails((prev) => [
            ...prev,
            { 
                uuid: airportUuid, 
                beaconId: "", 
                nickname: "",
                major: "", 
                minor: "", 
                notes: "", 
                beaconPlacement: "",
                iBeaconMacAddress: ""
            }
        ]);
    };

    // Remove a beacon by index
    const handleRemoveBeacon = (index) => {
        setBeaconDetails((prev) => prev.filter((_, i) => i !== index));
    };

    // Update beacon field
    const handleBeaconChange = (index, field, value) => {
        setBeaconDetails((prev) => {
            let newDetails = [...prev];
            const processedValue =
                field === 'uuid' ? value.trim().toLowerCase() :
                field === 'iBeaconMacAddress' ? value.trim().toUpperCase() :
                (field === 'major' || field === 'minor') ? Number(value) :
                value; // For beaconId, notes, and beaconPlacement, keep as string
            
            newDetails[index] = { 
                ...newDetails[index], 
                [field]: processedValue
            };
            return newDetails;
        });
    };

    return (
        <Row className='mt-5'>
            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, display: "flex", justifyContent: "space-between", marginTop: Spacing.SCALE_16 }}>
                <p style={{ margin: 0, padding: 0, height: "24px" }}>Beacon Details</p>
                <p style={{ color: 'grey', margin: 0, padding: 0, height: "24px", fontSize:12 }}>{"(Optional)"}</p>
            </Form.Label>

            <div style={{ display:'flex', justifyContent:'space-between'}}>
                <Subtitle>Add all beacons for this location.</Subtitle>
            </div>

            <div className=''>
                {beaconDetails.map((beacon, index) => (
                    <div key={index} style={{ 
                        marginTop: index === 0 ? Spacing.SCALE_8 : Spacing.SCALE_16, 
                        padding: '15px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '8px',
                        backgroundColor: '#f9f9f9'
                    }}>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                            <h6 style={{ margin: 0, color: Colors.PRM_LIGHT_BLUE }}>Beacon {index + 1}</h6>
                            <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => handleRemoveBeacon(index)}
                                style={{ minWidth: '40px' }}
                            >
                                ✕
                            </Button>
                        </div>

                        {/* UUID and Beacon ID row */}
                        <div className='d-flex gap-2 mb-2'>
                            <div style={{ flex: 1 }}>
                                <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>Beacon UUID</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Beacon UUID"
                                    name="uuid"
                                    onChange={(e) => handleBeaconChange(index, 'uuid', e.target.value)}
                                    value={airportUuid || ""}
                                    disabled={true}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>Beacon ID</Form.Label>
                                    <span style={{ color: 'red', fontSize: '10px', marginBottom: '4px' }}>* Required</span>
                                </div>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Beacon ID"
                                    name="beaconId"
                                    onChange={(e) => handleBeaconChange(index, 'beaconId', e.target.value)}
                                    value={beacon?.beaconId || ""}
                                />
                            </div>
                        </div>

                        {/* Nickname row */}
                        <div className='mb-2'>
                            <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>Nickname</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter a nickname for this beacon"
                                name="nickname"
                                onChange={(e) => handleBeaconChange(index, 'nickname', e.target.value)}
                                value={beacon?.nickname || ""}
                            />
                        </div>

                        {/* Major and Minor row */}
                        <div className='d-flex gap-2 mb-2'>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>Major</Form.Label>
                                    <span style={{ color: 'red', fontSize: '10px', marginBottom: '4px' }}>* Required</span>
                                </div>
                                <Form.Control
                                    type="number"
                                    placeholder="Major"
                                    name="major"
                                    onChange={(e) => handleBeaconChange(index, 'major', e.target.value)}
                                    value={beacon?.major || ""}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>Minor</Form.Label>
                                    <span style={{ color: 'red', fontSize: '10px', marginBottom: '4px' }}>* Required</span>
                                </div>
                                <Form.Control
                                    type="number"
                                    placeholder="Minor"
                                    name="minor"
                                    onChange={(e) => handleBeaconChange(index, 'minor', e.target.value)}
                                    value={beacon?.minor || ""}
                                />
                            </div>
                        </div>

                        {/* iBeacon Mac Address */}
                        <div className='mb-2'>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>iBeacon Mac Address</Form.Label>
                                <span style={{ color: 'grey', fontSize: '10px', marginBottom: '4px' }}>(Optional)</span>
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="A1:B1:C1:D1:E1:F1"
                                name="iBeaconMacAddress"
                                onChange={(e) => handleBeaconChange(index, 'iBeaconMacAddress', e.target.value)}
                                value={beacon?.iBeaconMacAddress || ""}
                                isInvalid={!!beacon?.iBeaconMacAddress && !MAC_ADDRESS_REGEX.test(beacon.iBeaconMacAddress)}
                            />
                            <Form.Control.Feedback type="invalid">
                                Enter a valid MAC address, e.g. A1:B1:C1:D1:E1:F1
                            </Form.Control.Feedback>
                        </div>

                        {/* Notes */}
                        <div className='mb-2'>
                            <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Add any notes about this beacon"
                                name="notes"
                                onChange={(e) => handleBeaconChange(index, 'notes', e.target.value)}
                                value={beacon?.notes || ""}
                            />
                        </div>

                        {/* Beacon Placement */}
                        <div>
                            <Form.Label style={{ fontSize: '12px', marginBottom: '4px' }}>Beacon Placement</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Beacon is under desk, on the wall, etc."
                                name="beaconPlacement"
                                onChange={(e) => handleBeaconChange(index, 'beaconPlacement', e.target.value)}
                                value={beacon?.beaconPlacement || ""}
                            />
                        </div>
                    </div>
                ))}

                <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={handleAddBeacon}
                    className='mt-3'
                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                    <span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span>
                    Add Beacon
                </Button>
                
            </div>
        </Row>
    );
};

export default LocationBeaconForm;

